import { BreakPointCondition, UI, useBreakpointIf, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigation } from '@react-navigation/native';
import { Box, ScrollView, Text } from 'native-base';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';

import { DynamicOrdinationProvider } from '../../contexts/DynamicOrdination';
import { ScrollCloseToBottomProvider } from '../../contexts/ScrollCloseToBottom';
import { FiltersToApplyProps, useFilterTransformer } from '../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useScrollCloseToBottom } from '../../hooks/useScrollCloseToBottom';
import { useToastCreator } from '../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../routes/types';
import { useOrderHistoryStateActions } from '../../store/orderHistory/actions';
import { orderHistoryByAtom, orderHistoryFiltersAtom } from '../../store/orderHistory/atom';
import {
  orderSequenceStatusQuery,
  ordersConsolidatedQuery,
  ordersHistoryQuery,
} from '../../store/orderHistory/selector';
import { OrderHistoryType } from '../../store/orderHistory/types';

import { getUpdatedAt } from './constants';
import { OrderHistoryMain } from './Container';
import { OrdersHistoryHeader } from './Container/components/OrdersHistoryHeader';
import { OrdersIndicators } from './Container/components/OrdersIndicators';
import {
  SearchFormData,
  defaultPagination,
  formSearchSchema,
  ordersHistoryTableHead,
} from './Container/constants';

export const OrdersHistory = () => {
  const orderHistoryForm = useForm<SearchFormData>({
    resolver: zodResolver(formSearchSchema),
    defaultValues: { searchTerm: '' },
    mode: 'all',
  });

  const { selectedOption: selectedFilter } = useOrderHistoryStateActions();

  useRecoilQuery(orderSequenceStatusQuery);

  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const { showToastFromError } = useToastCreator('order-history-toast');

  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const { isCloseToBottom, validateIsCloseToBottom } = useScrollCloseToBottom();

  const [lastUpdated, setLastUpdated] = useState('Carregando...');
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [pagination, setPagination] = useState(defaultPagination);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [orders, setOrders] = useState<OrderHistoryType[]>([]);

  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyProps>({});
  const [{ appliedFilters, availableFilters }] = useRecoilState(orderHistoryFiltersAtom);
  const [{ appliedOrderBy }] = useRecoilState(orderHistoryByAtom);

  const {
    data: ordersData,
    error,
    loading,
    refresher: refreshOrders,
  } = useRecoilQuery(
    ordersHistoryQuery({
      page: pagination.page,
      limit: pagination.limit,
      searchTerm,
      filtersToApply,
      orderBy: appliedOrderBy,
    })
  );

  const {
    data: ordersConsolidated,
    loading: loadingConsolidated,
    refresher: ordersConsolidatedRefresher,
    error: orderConsolidatedError,
  } = useRecoilQuery(ordersConsolidatedQuery({ searchTerm, filtersToApply }));

  const clearPaginationAndOrders = () => {
    setPagination({ ...defaultPagination });
    setOrders([]);
  };

  const handleSearchTerm = () => {
    clearPaginationAndOrders();
    setSearchTerm(orderHistoryForm.getValues().searchTerm);
  };

  const handleClearSearch = () => {
    clearPaginationAndOrders();
    setSearchTerm('');
    orderHistoryForm.reset({ searchTerm: '' });
  };

  const handleListMoreOrders = () => {
    if (loading) {
      return;
    }
    if (!hasNextPage) {
      return;
    }
    setPagination({ ...pagination, page: pagination.page + 1 });
  };

  const handleRefresh = () => {
    clearPaginationAndOrders();
    ordersConsolidatedRefresher();
    setRefreshing(true);
    setLastUpdated('Carregando...');
  };

  const { transformFilters } = useFilterTransformer(availableFilters);

  useEffect(() => {
    clearPaginationAndOrders();
    setFiltersToApply(
      transformFilters(appliedFilters, {
        transformDateFilter: false,
        transformRangeFieldNames: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    clearPaginationAndOrders();
  }, [appliedOrderBy]);

  useEffect(() => {
    setPagination(ordersData?.meta.pagination || defaultPagination);
    setHasNextPage(ordersData?.meta.pagination?.hasNext || false);
    if (ordersData?.meta.pagination.page === 1) {
      setOrders(ordersData.data);
    } else {
      setOrders([...orders, ...(ordersData?.data || [])]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersData]);

  useEffect(() => {
    if (error) {
      if (instanceOfErrorListResponse(error)) {
        (error as unknown as ErrorListResponse)?.errors?.forEach((e) => {
          showToastFromError(e);
        });
      } else {
        showToastFromError(error as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!loadingConsolidated) {
      setLastUpdated(getUpdatedAt(new Date()));
    }
  }, [loadingConsolidated]);

  useEffect(() => {
    clearPaginationAndOrders();
  }, [selectedFilter]);

  useEffect(() => {
    if (refreshing) {
      refreshOrders();
      setRefreshing(false);
    }
  }, [refreshing, refreshOrders]);

  // this prevents a one render empty state on loading
  const ordersToUse = orders.length > 0 ? orders : ordersData?.data || [];

  const orderHistoryMainProps = {
    isLoading: {
      all: loading && pagination.page === 1,
      more: loading && pagination.page > 1,
    },
    handleClearSearch,
    handleSearchTerm,
    data: {
      orders: ordersToUse,
    },
    listMoreOrders: handleListMoreOrders,
    isMobile,
    searchTerm,
    hasFilter: filtersToApply && Object.keys(filtersToApply).length > 0,
    onRefresh: handleRefresh,
  };

  const renderMainContainer = () => {
    if (isMobile) {
      return (
        <FormProvider {...orderHistoryForm}>
          <OrderHistoryMain {...orderHistoryMainProps} />;
        </FormProvider>
      );
    }

    return (
      <UI.Shadow level={1} testId="shadow" flex={1} borderTopRadius={Spacing['inline.nano']}>
        <Box w={'100%'} bg={Color['neutral.white']} borderTopRadius={Spacing['inline.nano']}>
          <Box
            px={Spacing['inline.xxxs']}
            py={'20.5px'}
            borderBottomWidth={0.5}
            borderBottomColor={Color['neutral.lightest']}>
            <HStack alignItems={'center'} justifyContent={'space-between'}>
              <Text
                color={Color['neutral.dark']}
                variant="semibold"
                fontSize={Typography['fontsize.md']}
                lineHeight={Typography['lineheight.distant']}>
                HISTÓRICO DE ORDENS
              </Text>
            </HStack>
          </Box>
          <VStack space={'33px'} px={Spacing['inline.xxxs']} py={Spacing['stack.xxs']}>
            <FormProvider {...orderHistoryForm}>
              <OrderHistoryMain {...orderHistoryMainProps} />
            </FormProvider>
          </VStack>
        </Box>
      </UI.Shadow>
    );
  };

  return (
    <ScrollView
      w="full"
      flex={1}
      scrollEventThrottle={200}
      onScroll={({ nativeEvent }) => validateIsCloseToBottom(nativeEvent)}
      px={Utils.Responsive.All({
        desktop: Spacing['inline.xxxs'],
        tablet: Spacing['inline.xxs'],
        phone: Spacing['inline.xxxs'],
      })}
      py={Utils.Responsive.PhoneTablet({
        tablet: 0,
        phone: Spacing['inline.xxxs'],
      })}>
      <ScrollCloseToBottomProvider isCloseToBottom={isCloseToBottom}>
        <DynamicOrdinationProvider
          orderAtom={orderHistoryByAtom}
          tableHead={ordersHistoryTableHead}>
          <VStack
            width="100%"
            maxWidth="1280px"
            minWidth="0"
            alignSelf="center"
            space={Utils.Responsive.PhoneTablet({
              phone: Spacing['stack.xxxs'],
              tablet: Spacing['stack.xxs'],
            })}
            {...(isMobile && { space: Spacing['stack.nano'] })}>
            <OrdersHistoryHeader
              isMobile={isMobile}
              lastUpdated={lastUpdated}
              onRefresh={handleRefresh}
              onReturn={() => navigate('Cockpit')}
            />
            <OrdersIndicators
              isMobile={isMobile}
              ordersConsolidated={ordersConsolidated?.data}
              orderConsolidatedLoading={loadingConsolidated}
              orderConsolidatedError={orderConsolidatedError}
            />
            {renderMainContainer()}
          </VStack>
        </DynamicOrdinationProvider>
      </ScrollCloseToBottomProvider>
    </ScrollView>
  );
};
